// File: frontend/src/components/WeekProgress.js

import { memo } from 'react';
import { TrendingUp, Moon } from 'lucide-react';
import { getWeekStart } from '../utils/weekCalculations';
import { formatLocalDate } from '../utils/colorUtils';

function WeekProgress({ habits, weekDates, completions, sickWeeks = {}, currentWeekStart, config, layoutMode }) {
  const today = new Date();
  const currentDayOfWeek = today.getDay() === 0 ? 7 : today.getDay(); // Convert Sunday to 7
  const daysElapsed = currentDayOfWeek; // Monday = 1, Tuesday = 2, etc.
  const daysRemaining = 7 - daysElapsed;

  // Don't render if not visible for this layout or no config passed
  if (!config || !config.visible) {
    return null;
  }

  // Get week start for sick weeks lookup
  const weekStart = weekDates.length > 0 ? getWeekStart(new Date(weekDates[0])) : null;
  const weekStartStr = weekStart ? formatLocalDate(weekStart) : null;

  // Helper to check if a habit is sick
  const isHabitSick = (habitId) => weekStartStr && sickWeeks[`${habitId}-${weekStartStr}`];

  // Calculate metrics for each habit
  const habitMetrics = habits.map(habit => {
    const isSick = isHabitSick(habit.id);
    const completedDays = weekDates.filter(date => completions[`${habit.id}-${date}`]).length;

    // If sick, treat as complete
    if (isSick) {
      return {
        ...habit,
        completedDays,
        remaining: 0,
        currentProgress: 100,
        likelihood: 100,
        status: 'resting',
        isSick: true
      };
    }

    const remaining = Math.max(0, habit.weekly_goal - completedDays);
    const currentProgress = (completedDays / habit.weekly_goal) * 100;

    // Likelihood calculation - can we still hit the goal?
    let likelihood;
    if (remaining === 0) {
      likelihood = 100; // Already completed goal
    } else if (remaining > daysRemaining) {
      likelihood = 0; // Impossible - need more completions than days left
    } else {
      // Possible - scale by how tight the schedule is
      // e.g., 1 remaining with 3 days left = 100%, 2 remaining with 2 days = 50%
      likelihood = Math.round(((daysRemaining - remaining + 1) / daysRemaining) * 100);
    }

    return {
      ...habit,
      completedDays,
      remaining,
      currentProgress,
      likelihood: Math.round(likelihood),
      status: remaining === 0 ? 'complete' : remaining <= daysRemaining ? 'on-track' : 'behind',
      isSick: false
    };
  });

  // Overall metrics - cap completions at each habit's weekly goal
  const totalCompletions = habitMetrics.reduce((sum, h) => sum + Math.min(h.completedDays, h.weekly_goal), 0);
  const totalGoals = habits.reduce((sum, h) => sum + h.weekly_goal, 0);
  const overallProgress = totalGoals > 0 ? (totalCompletions / totalGoals) * 100 : 0;

  const habitsOnTrack = habitMetrics.filter(h => h.status === 'on-track' || h.status === 'complete' || h.status === 'resting').length;
  const habitsResting = habitMetrics.filter(h => h.status === 'resting').length;


  // Overall likelihood (weighted by each habit's weekly goal)
  const overallLikelihood = totalGoals > 0
    ? Math.round(habitMetrics.reduce((sum, h) => sum + (h.likelihood * h.weekly_goal), 0) / totalGoals)
    : 0;

  // Stripped variant for phone portrait: compact horizontal card with just
  // the progress ring + success-likelihood bar. No title, no breakdown grid.
  if (config.variant === 'stripped') {
    return (
      <div className={`glass-morphism rounded-3xl ${config.padding} bg-[var(--color-paper-warm)]/40 border border-[var(--color-ink-subtle)]/10 flex items-center space-x-4`}>
        {/* Progress ring */}
        <div className={`relative ${config.progressSize} flex-shrink-0 flex items-center justify-center`}>
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="70" stroke="var(--color-divider)" strokeWidth="10" fill="none" />
            <circle
              cx="80"
              cy="80"
              r="70"
              stroke={overallProgress >= 70 ? '#16a34a' : overallProgress >= 40 ? '#d97706' : '#dc2626'}
              strokeWidth="10"
              fill="none"
              strokeDasharray={`${(overallProgress / 100) * 439.82} 439.82`}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <span className={`absolute font-serif font-black text-[var(--color-ink-main)] ${config.textSize}`}>
            {Math.round(overallProgress)}%
          </span>
        </div>
        {/* Supporting stats */}
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-xs font-medium text-[var(--color-ink-light)] uppercase tracking-wider">Likelihood</span>
              <span className={`font-serif font-bold text-sm ${overallLikelihood >= 70 ? 'text-green-600' : overallLikelihood >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                {overallLikelihood}%
              </span>
            </div>
            <div className="w-full bg-[var(--color-paper-bg)] rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-1000 ${overallLikelihood >= 70 ? 'bg-green-500' : overallLikelihood >= 40 ? 'bg-amber-400' : 'bg-red-400'}`}
                style={{ width: `${overallLikelihood}%` }}
              />
            </div>
          </div>
          <p className="text-xs text-[var(--color-ink-light)] font-sans">
            {totalCompletions} / {totalGoals} habits completed
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <div className="glass-morphism rounded-3xl p-6 bg-[var(--color-paper-warm)]/40 border border-[var(--color-ink-subtle)]/10">
        <h3 className="font-serif font-bold text-xl text-[var(--color-ink-main)] mb-4 flex items-center space-x-2">
          <TrendingUp className="w-5 h-5 text-[var(--color-ink-light)]" />
          Analysis
        </h3>

        {/* Progress Circle */}
        <div className="flex flex-col items-center mb-6">
          <div className={`relative ${config.progressSize} flex items-center justify-center`}>
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke="var(--color-divider)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke={overallProgress >= 70 ? "#16a34a" : overallProgress >= 40 ? "#d97706" : "#dc2626"}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${(overallProgress / 100) * 439.82} 439.82`}
                strokeLinecap="round"
                className="transition-all duration-1500 ease-out"
              />
            </svg>
            <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center">
              <span className={`font-serif font-black text-[var(--color-ink-main)] ${layoutMode === 'expanded' ? 'text-5xl' : 'text-4xl'}`}>
                {Math.round(overallProgress)}%
              </span>
              <span className="text-xs font-sans uppercase tracking-wider text-[var(--color-ink-light)]">Weekly Goal</span>
            </div>
          </div>

          <div className="text-center mt-2">
            <p className="text-[var(--color-ink-light)] font-sans text-sm">
              {totalCompletions} / {totalGoals} habits completed
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="space-y-4">
          {/* Success Likelihood */}
          <div className="bg-[var(--color-surface-card)] rounded-xl p-4 border border-[var(--color-divider)] shadow-sm">
            <div className="flex justify-between items-end mb-2">
              <span className="text-sm font-medium text-[var(--color-ink-light)]">Success Likelihood</span>
              <span className={`font-serif font-bold text-lg ${overallLikelihood >= 70 ? 'text-green-600' :
                overallLikelihood >= 40 ? 'text-amber-500' : 'text-red-500'
                }`}>
                {overallLikelihood}%
              </span>
            </div>
            <div className="w-full bg-[var(--color-paper-bg)] rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-1000 ${overallLikelihood >= 70 ? 'bg-green-500' :
                  overallLikelihood >= 40 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                style={{ width: `${overallLikelihood}%` }}
              />
            </div>
          </div>

          {/* Breakdown */}
          <div className="flex space-x-3">
            <div className="flex-1 bg-[var(--color-surface-card)] rounded-xl p-3 border border-[var(--color-divider)] shadow-sm flex flex-col items-center justify-center">
              <span className="text-xs text-[var(--color-ink-light)] uppercase tracking-wider mb-1">On Track</span>
              <span className="font-serif font-bold text-2xl text-[var(--color-ink-main)]">{habitsOnTrack - habitsResting}</span>
            </div>
            {habitsResting > 0 && (
              <div className="flex-1 bg-amber-50 rounded-xl p-3 border border-amber-200/50 shadow-sm flex flex-col items-center justify-center">
                <span className="text-xs text-amber-600 uppercase tracking-wider mb-1 flex items-center space-x-1">
                  <Moon size={10} className="fill-current" /> Resting
                </span>
                <span className="font-serif font-bold text-2xl text-amber-600">{habitsResting}</span>
              </div>
            )}
            <div className="flex-1 bg-[var(--color-surface-card)] rounded-xl p-3 border border-[var(--color-divider)] shadow-sm flex flex-col items-center justify-center">
              <span className="text-xs text-[var(--color-ink-light)] uppercase tracking-wider mb-1">Attention</span>
              <span className="font-serif font-bold text-2xl text-red-500">{habits.length - habitsOnTrack}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(WeekProgress);